/**
 * Checklist Data Transformation Utilities
 * Functions for converting between frontend and backend checklist data formats
 */

// export const transformChecklistForLambda = (frontendData) => {
//   // Extract required fields with defaults
//   const templateId = frontendData.templateId || "";
//   const templateVersion = frontendData.templateVersion || 1;
//   const serviceAppointmentNumber = frontendData.serviceAppointmentNumber || "";
//   const checklistStatus = frontendData.status || "InProgress";
//   const assignedTo = frontendData.assignedTo || "";

//   // Extract checklist content fields
//   const name = frontendData.name || "Untitled Checklist";
//   const description = frontendData.description || "";
//   const isActive =
//     frontendData.isActive !== undefined ? frontendData.isActive : true;
//   const items = frontendData.items || [];

//   // Validate required fields
//   if (!templateId.trim()) {
//     throw new Error("Template ID is required");
//   }
//   if (!serviceAppointmentNumber.trim()) {
//     throw new Error("Service appointment number is required");
//   }

//   // Transform to Lambda expected format
//   const lambdaData = {
//     templateId: templateId.trim(),
//     templateVersion: templateVersion,
//     serviceAppointmentNumber: serviceAppointmentNumber.trim(),
//     checklistStatus: checklistStatus,
//     assignedTo: assignedTo,
//     checklistContent: {
//       name: name,
//       description: description,
//       isActive: isActive,
//       items: items.map(transformItemForLambda),
//       metadata: frontendData.metadata || {},
//     },
//   };

//   return lambdaData;
// };

/**
 * Transforms frontend checklist data to Lambda-compatible format for saving
 * @param {Object} frontendData - Checklist data from frontend
 * @returns {Object} Lambda-compatible checklist data
 */
export const transformChecklistForLambda = (frontendData) => {
  const templateConfig = frontendData.templateConfig || {};
  // const items = templateConfig.items || [];
  //  TODO: TO CHANGE IN THE FUTURE
  const fsmAssets = frontendData.fsmAssets || [];
  let fsmAssetsForPdf = [];

  if (fsmAssets.length > 0) {
    fsmAssetsForPdf = fsmAssets.map((asset) => {
      const assetInfo = {};
      const assetItems =
        asset.template_config?.items.filter(
          (item) => item.description.toLowerCase().indexOf("asset") > -1
        ) || [];
      if (assetItems.length > 0) {
        const assetInfoKeys = [
          "type",
          "placering",
          "producent",
          "anden id",
          "model",
        ];
        assetInfoKeys.forEach((info) => {
          const assetInfoValue =
            assetItems.find(
              (item) => item.description.toLowerCase().indexOf(info) > -1
            )?.textValue || "";
          if (info === "producent") {
            assetInfo["asset_producer"] = assetInfoValue;
          } else {
            assetInfo[`asset_${info.trim().replace(" ", "_")}`] =
              assetInfoValue;
          }
        });
      }

      return Object.keys(assetInfo).length > 0
        ? { ...asset, ...assetInfo }
        : asset;
    });
  } else {
    fsmAssetsForPdf = fsmAssets;
  }

  return {
    // Checklist metadata (from database)
    id: frontendData.id || "",
    template_id: frontendData.templateId || "",
    template_version: frontendData.templateVersion || 1,
    service_appointment_number: frontendData.serviceAppointmentNumber || "",
    assigned_to: frontendData.assignedTo || "",
    status: frontendData.status || "InProgress",
    createdAt: frontendData.createdAt || new Date().toISOString(),
    updatedAt: frontendData.updatedAt || new Date().toISOString(),
    layoutFile: frontendData.layoutFile || "",

    // Checklist content (from S3 template_config)
    templateName: templateConfig.name || "Untitled Checklist",
    description: templateConfig.description || "",
    isActive:
      templateConfig.isActive !== undefined ? templateConfig.isActive : true,

    // Where the data inserted data for the asset will be saved
    template_config: templateConfig,
    fsm_assets: fsmAssetsForPdf || frontendData.fsmAssets || [],

    // Metadata structure for compatibility
    metadata: {
      category: templateConfig.category || "general",
      tags: templateConfig.tags || [],
      countries: templateConfig.countries || [],
      languages: templateConfig.languages || [],
    },
  };
};

/**
 * Transforms a frontend checklist item to Lambda-compatible format
 * @param {Object} item - Frontend item object
 * @returns {Object} Lambda-compatible item
 */
export const transformItemForLambda = (item) => {
  return {
    id:
      item.id ||
      `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    type: item.type || "checkbox",
    label: item.label || "",
    description: item.description || item.label || "",
    required: Boolean(item.required),
    order: item.order !== undefined ? item.order : 0,
    defaultValue: item.defaultValue || {},
    config: item.config || {},
    // Include completion data if present
    response: item.response || null,
    completed: Boolean(item.completed),
    completedAt: item.completedAt || null,
  };
};

/**
 * Transforms Lambda checklist response back to frontend format
 * @param {Object} lambdaResponse - Response from Lambda function
 * @returns {Object} Frontend-compatible checklist data
 */
export const transformLambdaResponseToFrontend = (lambdaResponse) => {
  if (!lambdaResponse) {
    throw new Error("Invalid Lambda response format");
  }

  // Handle different response formats
  let checklists = [];

  if (lambdaResponse.checklist && Array.isArray(lambdaResponse.checklist)) {
    checklists = lambdaResponse.checklist;
  } else if (Array.isArray(lambdaResponse)) {
    checklists = lambdaResponse;
  } else if (lambdaResponse.checklist) {
    checklists = [lambdaResponse.checklist];
  } else {
    // Return empty structure if no valid data
    return createEmptyChecklistStructure();
  }

  // Return the first checklist (most common case)
  const checklist = checklists[0];
  if (!checklist) {
    return createEmptyChecklistStructure();
  }

  return transformSingleChecklistFromLambda(checklist);
};

/**
 * Transform a single Lambda checklist to frontend format
 * @param {Object} lambdaChecklist - Lambda checklist data
 * @returns {Object} Frontend checklist structure
 */
export const transformSingleChecklistFromLambda = (lambdaChecklist) => {
  // Extract template_config (S3 data) and merge with metadata
  const templateConfig = lambdaChecklist.template_config || {};
  // const items = templateConfig.items || [];

  return {
    // Checklist metadata (from database)
    id: lambdaChecklist.id || "",
    templateId: lambdaChecklist.template_id || "",
    templateVersion: lambdaChecklist.template_version || 1,
    serviceAppointmentNumber: lambdaChecklist.service_appointment_number || "",
    assignedTo: lambdaChecklist.assigned_to || "",
    status: lambdaChecklist.status || "InProgress",
    createdAt: lambdaChecklist.createdAt || new Date().toISOString(),
    updatedAt: lambdaChecklist.updatedAt || new Date().toISOString(),
    layoutFile: lambdaChecklist.layoutFile || "",

    // Checklist content (from S3 template_config)
    templateName: templateConfig.name || "Untitled Checklist",
    description: templateConfig.description || "",
    isActive:
      templateConfig.isActive !== undefined ? templateConfig.isActive : true,

    templateConfig: templateConfig,
    // Where the data inserted data for the asset will be saved
    fsmAssets:
      lambdaChecklist.fsm_assets.map((fsmAsset) =>
        transformFsmAssetFromLambda(fsmAsset)
      ) || [],

    // Metadata structure for compatibility
    metadata: {
      category: templateConfig.category || "general",
      tags: templateConfig.tags || [],
      countries: templateConfig.countries || [],
      languages: templateConfig.languages || [],
    },
  };
};

/**
 * Transform Lambda item to frontend format
 * @param {Object} fsmAssetFromLambda - Lambda FSM asset data
 * @returns {Object} Frontend fsm Asset structure
 */
export const transformFsmAssetFromLambda = (fsmAsset) => {
  // const assetSerialNumber = fsmAsset.asset_serial_number || "";
  const workOrderLineItemId = fsmAsset.work_order_line_item_id || "";
  const templateConfig = fsmAsset.template_config;
  const transformedFsmAsset = {
    ...fsmAsset,
    template_config: {
      ...templateConfig,
      items: templateConfig.items.map((item) => {
        return {
          ...item,
          id:
            item.id.indexOf(workOrderLineItemId) === -1
              ? item.id + "_" + workOrderLineItemId
              : item.id,
        };
      }),
    },
  };
  return transformedFsmAsset;
};

/**
 * Transform Lambda item to frontend format
 * @param {Object} lambdaItem - Lambda item data
 * @returns {Object} Frontend item structure
 */
export const transformItemFromLambda = (lambdaItem) => {
  return {
    id:
      lambdaItem.id ||
      `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    type: lambdaItem.type || "checkbox",
    label: lambdaItem.label || lambdaItem.description || "",
    description: lambdaItem.description || "",
    required: Boolean(lambdaItem.required),
    order: lambdaItem.order !== undefined ? lambdaItem.order : 0,
    defaultValue: lambdaItem.defaultValue || lambdaItem.default_value || {},
    config: lambdaItem.config || {},
    // Add any response/completion data if present
    response: lambdaItem.response || null,
    completed: Boolean(lambdaItem.completed),
    completedAt: lambdaItem.completedAt || lambdaItem.completed_at || null,
  };
};

/**
 * Create an empty checklist structure
 * @returns {Object} Empty checklist structure
 */
export const createEmptyChecklistStructure = () => {
  return {
    id: "",
    templateId: "",
    templateVersion: "",
    serviceAppointmentNumber: "",
    assignedTo: "",
    status: "InProgress",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    layoutFile: "",
    name: "New Checklist",
    description: "",
    isActive: true,
    fsmAssets: [],
    metadata: {
      estimatedTimeMinutes: 15,
      category: "general",
      tags: [],
      countries: [],
      languages: [],
    },
  };
};

/**
 * Validates frontend checklist data before transformation
 * @param {Object} checklistData - Frontend checklist data
 * @returns {Object} Validation result { isValid: boolean, errors: string[] }
 */
export const validateChecklistForLambda = (checklistData) => {
  const errors = [];

  // Required field validation
  if (!checklistData.templateId || !checklistData.templateId.trim()) {
    errors.push("Template ID is required");
  }

  if (!checklistData.templateVersion) {
    errors.push("Template version is required");
  }

  if (
    !checklistData.serviceAppointmentNumber ||
    !checklistData.serviceAppointmentNumber.trim()
  ) {
    errors.push("Service appointment number is required");
  }

  // Items validation
  if (!Array.isArray(checklistData.fsmAssets)) {
    errors.push("Checklist items must be an array");
  } else if (checklistData.fsmAssets.length === 0) {
    errors.push("Checklist must have at least one item");
  } else {
    // Validate each Assets
    checklistData.fsmAssets.forEach((item, index) => {
      if (!item.asset_serial_number || !item.asset_serial_number.trim()) {
        errors.push(`Asset ${index + 1}: Asset Serial Number is required`);
      }
      if (!item.template_config || item.template_config.items.length === 0) {
        errors.push(`Asset ${index + 1}: Asset forms are required`);
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Transform query parameters from frontend to backend format
 * @param {Object} frontendParams - Frontend query parameters
 * @returns {Object} Backend-compatible parameters
 */
export const transformQueryParamsForBackend = (frontendParams) => {
  return {
    templateId: frontendParams.templateId, // Frontend templateId maps to backend templateId
    serviceAppointmentNumber: frontendParams.saId, // Frontend saId maps to backend serviceAppointmentNumber
    templateVersion: frontendParams.templateVersion,
  };
};

export default {
  transformChecklistForLambda,
  transformItemForLambda,
  transformLambdaResponseToFrontend,
  transformSingleChecklistFromLambda,
  transformItemFromLambda,
  createEmptyChecklistStructure,
  validateChecklistForLambda,
  transformQueryParamsForBackend,
};
